import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import LoadingState from "../components/LoadingState";
import { toast } from "sonner";
import { ScoreGauge } from "../components/ScoreGauge";

const STATUSES = [
    "Nuova", "In revisione", "In attesa di informazioni",
    "Da verificare con il fornitore", "Da verificare con il legale",
    "Escalata", "Falso positivo", "Chiusa",
];
const DEPARTMENTS = ["Amministrazione", "Acquisti", "Legale", "Sicurezza informatica",
    "IT/Sistemi", "Ufficio tecnico", "HR", "Direzione"];

export default function ReviewDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState("");
    const [showReclassify, setShowReclassify] = useState(false);
    const [showEscalate, setShowEscalate] = useState(false);
    const [newRisk, setNewRisk] = useState(50);
    const [reason, setReason] = useState("");
    const [dept, setDept] = useState(DEPARTMENTS[0]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/reviews/${id}`);
            setData(data);
            if (data?.risk?.operational_risk != null) setNewRisk(data.risk.operational_risk);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore caricamento");
        } finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = async (status, motiv) => {
        try {
            await api.patch(`/reviews/${id}/status`, { status, reason: motiv || undefined });
            toast.success("Stato aggiornato");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };
    const assignToMe = async () => {
        try {
            await api.post(`/reviews/${id}/assign`, { user_id: null });
            // Re-do with actual user id
            const me = data?.review?.assigned_to;
            const { data: profile } = await api.get("/auth/me");
            await api.post(`/reviews/${id}/assign`, { user_id: profile.user.id });
            toast.success("Assegnato a te");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };
    const addNote = async () => {
        if (!note.trim()) return;
        try {
            await api.post(`/reviews/${id}/notes`, { text: note });
            setNote("");
            toast.success("Nota aggiunta");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };
    const reclassify = async () => {
        if (!reason.trim()) { toast.error("Motivo obbligatorio"); return; }
        try {
            await api.post(`/reviews/${id}/reclassify`, { new_operational_risk: newRisk, reason });
            toast.success("Rischio operativo aggiornato");
            setShowReclassify(false); setReason(""); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };
    const escalate = async () => {
        if (!reason.trim()) { toast.error("Motivo obbligatorio"); return; }
        try {
            await api.post(`/reviews/${id}/escalate`, { to_department: dept, reason });
            toast.success("Escalation registrata");
            setShowEscalate(false); setReason(""); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };
    const markFP = async () => {
        const m = window.prompt("Motivo per falso positivo:");
        if (!m) return;
        try {
            await api.post(`/reviews/${id}/mark-false-positive`, { reason: m });
            toast.success("Segnalata come falso positivo");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    if (loading || !data) return <LoadingState />;
    const { review, pec, risk, notes, audit } = data;

    return (
        <div className="space-y-6" data-testid="review-detail">
            <PageHeader
                title={review.status}
                subtitle={pec?.subject || "PEC senza oggetto"}
                breadcrumbs={[{ label: "Coda revisioni", to: "/reviews" }, { label: review.status }]}
                actions={(
                    <div className="flex flex-wrap items-center gap-2">
                        <button data-testid="review-assign-me" onClick={assignToMe}
                            className="text-xs px-3 py-1.5 rounded-md border border-[#DCE3EE] bg-white">Assegna a me</button>
                        <select data-testid="review-status-select" value={review.status}
                            onChange={(e) => {
                                const s = e.target.value;
                                if (["Falso positivo", "Escalata"].includes(s)) {
                                    const r = window.prompt(`Motivo per "${s}":`);
                                    if (r) changeStatus(s, r);
                                } else { changeStatus(s); }
                            }}
                            className="text-xs px-2 py-1.5 rounded-md border border-[#DCE3EE] bg-white">
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button data-testid="review-reclassify" onClick={() => setShowReclassify(true)}
                            className="text-xs px-3 py-1.5 rounded-md border border-[#DCE3EE] bg-white">Reclassifica</button>
                        <button data-testid="review-escalate" onClick={() => setShowEscalate(true)}
                            className="text-xs px-3 py-1.5 rounded-md border border-[#DCE3EE] bg-white">Escala</button>
                        <button data-testid="review-mark-fp" onClick={markFP}
                            className="text-xs px-3 py-1.5 rounded-md border border-[#DCE3EE] bg-white text-[#DC2626]">Falso positivo</button>
                    </div>
                )}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-[#172033] mb-3">Riepilogo PEC</h3>
                        <div className="text-xs space-y-1">
                            <div><span className="text-[#64748B]">Oggetto:</span> {pec?.subject || "—"}</div>
                            <div><span className="text-[#64748B]">Mittente:</span> {pec?.from_address}</div>
                            <div><span className="text-[#64748B]">Autenticità tecnica:</span> {pec?.technical_status}</div>
                        </div>
                        {risk && (
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
                                <ScoreGauge label="Tecnica" value={risk.technical_authenticity} />
                                <ScoreGauge label="Mittente" value={risk.sender_reliability} />
                                <ScoreGauge label="Contenuto" value={risk.content_risk} />
                                <ScoreGauge label="Allegati/Link" value={risk.attachments_links_risk} />
                                <ScoreGauge label="Operativo" value={risk.operational_risk} />
                            </div>
                        )}
                        <Link to={`/pec/analyses/${pec?.id}`} data-testid="review-open-pec"
                            className="mt-3 inline-block text-xs text-[#2563EB] hover:underline">
                            Apri dettaglio PEC completo →
                        </Link>
                    </div>

                    <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-[#172033] mb-3">Note</h3>
                        <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="review-notes-timeline">
                            {notes.length === 0 && <div className="text-xs text-[#64748B]">Nessuna nota</div>}
                            {notes.map((n) => (
                                <div key={n.id} data-testid={`note-${n.id}`} className="border-l-2 border-[#2563EB] pl-3">
                                    <div className="text-[11px] text-[#64748B]">{n.user_email} · {n.created_at?.slice(0, 16).replace("T", " ")}</div>
                                    <div className="text-xs whitespace-pre-wrap">{n.text}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex gap-2">
                            <textarea data-testid="review-note-input" rows={2}
                                value={note} onChange={(e) => setNote(e.target.value)}
                                placeholder="Aggiungi una nota…"
                                className="flex-1 text-xs border border-[#DCE3EE] rounded-md px-2 py-1.5" />
                            <button data-testid="review-note-submit" onClick={addNote}
                                className="text-xs px-3 py-1.5 rounded-md bg-[#2563EB] text-white h-fit">Pubblica</button>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-[#172033] mb-3">Cronologia review</h3>
                        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="review-audit">
                            {audit.length === 0 && <div className="text-xs text-[#64748B]">Nessun evento registrato</div>}
                            {audit.map((a, i) => (
                                <div key={a.id || i} className="text-xs border-l-2 border-[#DCE3EE] pl-2">
                                    <div className="text-[11px] text-[#64748B]">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</div>
                                    <div className="font-medium">{a.action}</div>
                                    {a.note && <div className="text-[#64748B]">{a.note}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {showReclassify && (
                <Modal onClose={() => setShowReclassify(false)} title="Reclassifica rischio operativo">
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs text-[#64748B]">Nuovo valore (0-100): {newRisk}</label>
                            <input data-testid="reclassify-slider" type="range" min="0" max="100" value={newRisk}
                                onChange={(e) => setNewRisk(Number(e.target.value))} className="w-full" />
                        </div>
                        <textarea data-testid="reclassify-reason" rows={3} placeholder="Motivo (obbligatorio)"
                            value={reason} onChange={(e) => setReason(e.target.value)}
                            className="w-full text-xs border border-[#DCE3EE] rounded-md p-2" />
                        <button data-testid="reclassify-submit" onClick={reclassify}
                            className="w-full text-sm px-3 py-2 rounded-md bg-[#2563EB] text-white">Salva</button>
                    </div>
                </Modal>
            )}

            {showEscalate && (
                <Modal onClose={() => setShowEscalate(false)} title="Escala revisione">
                    <div className="space-y-3">
                        <select data-testid="escalate-dept" value={dept} onChange={(e) => setDept(e.target.value)}
                            className="w-full text-xs border border-[#DCE3EE] rounded-md p-2">
                            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <textarea data-testid="escalate-reason" rows={3} placeholder="Motivo (obbligatorio)"
                            value={reason} onChange={(e) => setReason(e.target.value)}
                            className="w-full text-xs border border-[#DCE3EE] rounded-md p-2" />
                        <button data-testid="escalate-submit" onClick={escalate}
                            className="w-full text-sm px-3 py-2 rounded-md bg-[#DC2626] text-white">Escala</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function Modal({ children, title, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-[#172033]">{title}</h4>
                    <button onClick={onClose} className="text-[#64748B] hover:text-[#172033]">✕</button>
                </div>
                {children}
            </div>
        </div>
    );
}
